// Rótulos e formatadores da rubrica, compartilhados.
//
// Moram num arquivo próprio porque, desde a extração do classificador
// (`motor-rubricas-classificar`), duas telas os usam. Deixá-los em
// `motor-rubricas.tsx` e importar de lá fecharia um ciclo — o motor já importa o
// classificador.

import { ROTULO_CATEGORIA_LEGAL, tipoServico } from "@atlas/domain";
import type {
  AdminCategoriaLegal,
  AdminDestinoMargem,
  AdminRubricaBucket,
  AdminRubricaVinculo,
  ConfiguracaoMargem,
  MargemAplicada,
  RegraDeMargemDto,
} from "@atlas/sdk";

/**
 * A categoria legal (ADR-0015, #270), o vocabulário que substitui a "natureza"
 * na tela — os antigos `NATUREZA_LABEL`/`NATUREZA_PILL` saíram com ela. Os rótulos são os do domínio — a mesma cópia que a API usa na
 * auditoria —, tipados pelo espelho do SDK.
 */
export const CATEGORIA_LABEL: Record<AdminCategoriaLegal, string> = ROTULO_CATEGORIA_LEGAL;

export const CATEGORIA_PILL: Record<AdminCategoriaLegal, "aceita" | "emdia" | "expirado" | "pendente"> = {
  consignacao_credito: "aceita",
  consignacao_facultativa: "emdia",
  provento: "emdia",
  desconto_obrigatorio: "expirado",
  desconto_nao_consignado: "expirado",
  nao_classificada: "pendente",
};

/** O rótulo do tipo de serviço pelo código do catálogo; código desconhecido aparece cru. */
export function rotuloTipoServico(codigo: string): string {
  return tipoServico(codigo)?.rotulo ?? codigo;
}

/**
 * "Produto", não "balde" — e SEM PERCENTUAL.
 *
 * O termo interno era `balde de margem` e chegava cru na tela. Quem opera a
 * folha não tem por que aprender a metáfora do time de dentro.
 *
 * O rótulo já foi "Empréstimo (35%)". Depois da ADR-0012 o número é do órgão, não
 * do produto: a ALECE trabalha com margem informada e saldo compartilhado, Morada
 * Nova com 40/5/15. Um percentual no nome do produto diz a quem classifica uma
 * coisa que o órgão não diz. A regra aparece à parte, lida do cadastro
 * (`descreverRegraDoOrgao`). Esta é a ÚNICA cópia do mapa — as outras telas
 * importam daqui.
 */
export const PRODUTO_LABEL: Record<AdminRubricaBucket, string> = {
  EMPRESTIMO: "Empréstimo",
  CARTAO_CONSIGNADO: "Cartão consignado",
  CARTAO_BENEFICIOS: "Cartão benefício",
};

/**
 * Para onde vai o desconto de um tipo de serviço na conta da margem (#271): os
 * três baldes com o MESMO rótulo de `PRODUTO_LABEL` (a cópia única), mais o
 * teto somado e o "fora da margem".
 */
export const DESTINO_LABEL: Record<AdminDestinoMargem, string> = {
  ...PRODUTO_LABEL,
  TOTAL: "Teto somado",
  NENHUM: "Não ocupa margem",
};

/** Fração → pontos percentuais legíveis: 0.4 → "40%", 0.125 → "12,5%". */
function pct(fracao: number): string {
  return `${String(Math.round(fracao * 10000) / 100).replace(".", ",")}%`;
}

const ESCOPO_SALDO_LABEL: Record<ConfiguracaoMargem["escopoSaldo"], string> = {
  compartilhado: "saldo compartilhado entre produtos",
  por_produto: "saldo separado por produto",
  limites_com_teto: "limites por produto com teto somado",
};

/** O que `regraMargemDaOrganizacao` devolve — só o que a descrição usa. */
export interface RegraDaOrganizacao {
  competencia: string;
  vigente: MargemAplicada & { padrao: boolean };
  historico: Pick<RegraDeMargemDto, "situacao" | "fundamento">[];
}

/** A regra de percentual que vale (a cadastrada ou o padrão), por extenso. */
function descreverPercentuais(regra: RegraDaOrganizacao): string {
  const { percentuais, percentualTotal } = regra.vigente;
  if (regra.vigente.padrao) {
    return `padrão do sistema: ${pct(percentuais.EMPRESTIMO)} · ${pct(percentuais.CARTAO_CONSIGNADO)} · ${pct(
      percentuais.CARTAO_BENEFICIOS,
    )} (nenhuma regra cadastrada para esta organização)`;
  }
  const partes = [
    `${pct(percentuais.EMPRESTIMO)} empréstimo`,
    `${pct(percentuais.CARTAO_CONSIGNADO)} cartão consignado`,
    `${pct(percentuais.CARTAO_BENEFICIOS)} cartão benefício`,
  ];
  if (percentualTotal != null) partes.push(`teto ${pct(percentualTotal)}`);
  const fundamento = regra.historico.find((r) => r.situacao === "vigente")?.fundamento?.trim();
  return `${partes.join(" · ")}${fundamento ? ` (${fundamento})` : ""}`;
}

/**
 * A regra de margem DO ÓRGÃO, numa linha — o que o motor de rubricas mostra
 * acima da lista, no lugar do percentual que antes morava no nome do produto.
 *
 * Precedência:
 *   1. configuração de margem informada/híbrida (a ativa; senão a que cobre a
 *      competência). Inativa, a linha DIZ que está inativa e o que vale hoje —
 *      é o caso da ALECE, e esconder o padrão que de fato se aplica faria a
 *      tela afirmar uma regra que o cálculo ainda não usa;
 *   2. regra de percentuais cadastrada, com o fundamento legal;
 *   3. padrão do sistema, escrito como padrão (ver `organizacao-ficha.tsx`).
 */
export function descreverRegraDoOrgao(
  regra: RegraDaOrganizacao,
  configuracoes: Pick<
    ConfiguracaoMargem,
    "modalidade" | "ativa" | "escopoSaldo" | "competenciaInicio" | "competenciaFim"
  >[],
): string {
  const c = regra.competencia;
  const informadas = configuracoes.filter((x) => x.modalidade !== "calculada");
  const cobre = (x: (typeof informadas)[number]) =>
    x.competenciaInicio <= c && (x.competenciaFim == null || x.competenciaFim >= c);
  const cfg =
    informadas.find((x) => x.ativa) ??
    informadas.filter(cobre).sort((a, b) => b.competenciaInicio.localeCompare(a.competenciaInicio))[0];

  const percentuais = descreverPercentuais(regra);
  if (!cfg) return percentuais.charAt(0).toUpperCase() + percentuais.slice(1);

  const tipo = cfg.modalidade === "hibrida" ? "Margem híbrida" : "Margem informada pelo órgão";
  const base = `${tipo} · ${ESCOPO_SALDO_LABEL[cfg.escopoSaldo]}`;
  if (cfg.ativa) return base;
  return `${base} — cadastrada, ainda inativa; hoje vale: ${percentuais}`;
}

const MESES = ["", "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/** `202608` → `Ago/2026`. `000000` é a competência imemorial da semeadura. */
export function fmtCompetencia(c: string): string {
  if (!/^\d{6}$/.test(c)) return "—";
  if (c === "000000") return "desde sempre";
  return `${MESES[Number(c.slice(4, 6))] ?? "?"}/${c.slice(0, 4)}`;
}

/** A janela de uma vigência, em uma linha. */
export function fmtVigencia(v: AdminRubricaVinculo): string {
  const de = fmtCompetencia(v.competenciaInicial);
  return v.competenciaFinal ? `${de} → ${fmtCompetencia(v.competenciaFinal)}` : `${de} → hoje`;
}
